// THE OEM ORDER DOCUMENT -- Order Management O5 (D181 #9: printable /
// downloadable only; the app e-mails nothing).
//
// What we send an OEM: our identity, the OEM, the ship-to, and the lines at
// the price THE OEM CONFIRMED (frozen on the order). ⚠️ NEVER our customer's
// price, our margin, or anything of the quote's sell side -- the server does
// not even send them (get_oem_order; test_oem_orders pins it).
//
// Printing reuses the customer document's mechanism: body.printing-document
// hides the app and lets this page flow (index.css, @media print).

import { useEffect, useState } from "react";
import { getOemOrder, type OemOrder, type OemOrderIssuer } from "@/services/api";
import { formatDate, formatTimestampDate } from "@/lib/format";
import { money, leadTimeLabel } from "@/components/OemConfirmationsPanel";
import { colors, semanticColors, radius, spacing, heights, typography, layout, shadows } from "@/styles/tokens";

/** THE SHIP-TO AS PRINTED LINES (ux-guided-deal, D192 live issue).
 *
 *  A drop-ship order carries the quote's customer ship-to SNAPSHOT, which the
 *  server stores as JSON ({name, street, street2, city, zip, state, vat,
 *  contact}); a warehouse ship-to is plain text. Both are printed as lines.
 *  Anything that is not such an object is printed exactly as it came. */
export function shipToLines(raw: string | null | undefined): string[] {
  const text = (raw ?? "").trim();
  if (!text) return [];
  if (text.startsWith("{")) {
    try {
      const v = JSON.parse(text);
      if (v && typeof v === "object" && !Array.isArray(v)) {
        const s = (x: unknown) => (typeof x === "string" && x.trim() ? x.trim() : null);
        const cityLine = [s(v.city), s(v.zip)].filter(Boolean).join(" ");
        const contact = v.contact && typeof v.contact === "object"
          ? [s(v.contact.name), s(v.contact.email)].filter(Boolean).join(", ") : "";
        const lines = [s(v.name), s(v.street), s(v.street2), cityLine || null, s(v.state),
          s(v.vat) ? `GSTIN ${s(v.vat)}` : null, contact ? `Attn: ${contact}` : null];
        const out = lines.filter((l): l is string => !!l);
        if (out.length) return out;
      }
    } catch { /* not JSON: printed as it came */ }
  }
  return text.split("\n");
}

export function OemOrderDocument({ orderId, onClose }: { orderId: number; onClose: () => void }) {
  const [order, setOrder] = useState<OemOrder | null>(null);
  const [issuer, setIssuer] = useState<OemOrderIssuer | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    void (async () => {
      const res = await getOemOrder(orderId);
      if (!res.ok) { setError(res.error || "The OEM order could not be read."); return; }
      setOrder(res.order!); setIssuer(res.issuer ?? null);
    })();
  }, [orderId]);

  useEffect(() => {
    document.body.classList.add("printing-document");
    return () => document.body.classList.remove("printing-document");
  }, []);

  const total = order ? order.lines.reduce((s, l) => s + l.qty_ordered * l.unit_price, 0) : 0;
  const cell: React.CSSProperties = { padding: `${spacing[6]}px ${spacing[8]}px`, borderBottom: `1px solid ${semanticColors.border}`, fontSize: typography.fontSize.sm, textAlign: "left" };

  return (
    <div data-testid="oem-order-doc-backdrop" role="dialog" aria-modal="true" style={{
      position: "fixed", inset: 0, backgroundColor: semanticColors.overlay, overflowY: "auto",
      display: "flex", flexDirection: "column", alignItems: "center", zIndex: 1000,
    }}>
      <div data-testid="oem-order-doc-toolbar" style={{
        position: "sticky", top: 0, display: "flex", gap: spacing[8], justifyContent: "flex-end",
        width: layout.pdfPageWidth, padding: `${spacing[10]}px 0`,
      }}>
        <button type="button" data-testid="oem-order-doc-print" onClick={() => window.print()} disabled={!order} style={{
          height: heights.control, padding: `0 ${spacing[14]}px`, backgroundColor: colors.indigo600,
          color: semanticColors.onAccent, border: "none", borderRadius: radius.base, cursor: "pointer",
          fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
        }}>Print / save as PDF</button>
        <button type="button" data-testid="oem-order-doc-close" onClick={onClose} style={{
          height: heights.control, padding: `0 ${spacing[14]}px`, backgroundColor: semanticColors.surface,
          color: semanticColors.textSecondary, border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
          cursor: "pointer", fontSize: typography.fontSize.base,
        }}>Close</button>
      </div>
      <div data-testid="oem-order-doc-page" style={{
        width: layout.pdfPageWidth, minHeight: layout.pdfPageMinHeight, backgroundColor: semanticColors.surface,
        boxShadow: shadows.modal, padding: spacing[40], marginBottom: spacing[24], color: semanticColors.textPrimary,
      }}>
        {error && <div role="alert" style={{ color: colors.red600 }}>{error}</div>}
        {order && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", gap: spacing[24] }}>
              <div data-testid="oem-order-doc-issuer" style={{ fontSize: typography.fontSize.sm, whiteSpace: "pre-line" }}>
                <div style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold }}>
                  {issuer?.company_name || "—"}
                </div>
                {issuer?.address}
                {issuer?.gstin ? `\nGSTIN ${issuer.gstin}` : ""}
                {issuer?.contact_email ? `\n${issuer.contact_email}` : ""}
                {issuer?.contact_phone ? `\n${issuer.contact_phone}` : ""}
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.semibold }}>Order to OEM</div>
                <div data-testid="oem-order-doc-number" style={{ fontSize: typography.fontSize.lg }}>{order.number}</div>
                <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
                  Date {order.placed_at ? formatTimestampDate(order.placed_at) : "—"} · Our ref. {order.quote_name}
                </div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[24], marginTop: spacing[24], fontSize: typography.fontSize.sm }}>
              <div><div style={{ color: semanticColors.textMuted }}>To</div><div style={{ fontWeight: typography.fontWeight.medium }}>{order.oem}</div></div>
              <div data-testid="oem-order-doc-shipto" style={{ whiteSpace: "pre-line" }}>
                <div style={{ color: semanticColors.textMuted }}>Ship to ({order.ship_to_kind === "customer" ? "customer site" : "our warehouse"})</div>
                {shipToLines(order.ship_to).length
                  ? shipToLines(order.ship_to).map((l, i) => <div key={i}>{l}</div>) : "—"}
              </div>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: spacing[24] }}>
              <thead>
                <tr>{["#", "Part", "Description", "Qty", `Unit price (${order.currency})`, "Amount", "Lead time", "Promised", "Your ref."].map((h) => (
                  <th key={h} style={{ ...cell, color: semanticColors.textMuted, fontWeight: typography.fontWeight.medium }}>{h}</th>))}</tr>
              </thead>
              <tbody>
                {order.lines.map((l, i) => (
                  <tr key={l.id} data-testid={`oem-order-doc-line-${i + 1}`}>
                    <td style={cell}>{i + 1}</td>
                    <td style={{ ...cell, whiteSpace: "nowrap" }}>{l.part_number}</td>
                    <td style={cell}>{l.description || "—"}</td>
                    <td style={cell}>{l.qty_ordered}</td>
                    <td style={{ ...cell, whiteSpace: "nowrap" }}>{money(l.unit_price, l.currency)}</td>
                    <td style={{ ...cell, whiteSpace: "nowrap" }}>{money(l.qty_ordered * l.unit_price, l.currency)}</td>
                    <td style={cell}>{leadTimeLabel({ lead_time: { ...l.lead_time, text: null } })}</td>
                    <td style={cell}>{l.promised_date ? formatDate(l.promised_date) : "Not stated"}</td>
                    <td style={cell}>{l.oem_reference || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div data-testid="oem-order-doc-total" style={{ textAlign: "right", marginTop: spacing[12], fontWeight: typography.fontWeight.semibold }}>
              Total {money(total, order.currency)}
            </div>
            {order.notes && <div style={{ marginTop: spacing[16], fontSize: typography.fontSize.sm, whiteSpace: "pre-line" }}>{order.notes}</div>}
            <div style={{ marginTop: spacing[32], fontSize: typography.fontSize.xs, color: semanticColors.textMuted }}>
              Prices as confirmed by {order.oem}. Placed by {order.placed_by ?? "—"}.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
